import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import banner1 from "../assets/banner1.jpg";
import banner2 from "../assets/banner2.jpg";
import banner3 from "../assets/banner3.jpg";
import banner4 from "../assets/banner4.jpg";
import banner5 from "../assets/banner5.jpg";
import banner6 from "../assets/banner6.jpg";
import { 
  FaEye, FaScaleBalanced, FaHandshake, FaLightbulb, 
  FaHandPointRight, FaShieldHeart, FaArrowRight 
} from "react-icons/fa6";

// --- Data ---
const coreValues = [
  { name: "Accountability", desc: "Own outcomes, deliver results" },
  { name: "Transparency", desc: "Clear communication, open processes" },
  { name: "Competence", desc: "Expert knowledge, continuous growth" },
  { name: "Confidentiality", desc: "Protecting your sensitive information" },
  { name: "Integrity", desc: "Ethical conduct in every engagement" },
  { name: "Objectivity", desc: "Unbiased insights, data-driven advice" },
  { name: "Professional Behaviour", desc: "Excellence in all interactions" }
];

const aboutImages = [banner1, banner2, banner3, banner4, banner5, banner6];

const pillars = [
  {
    title: "STRATEGIZE",
    desc: "Strategies aligned with your business vision and market dynamics.",
    img: banner1,
    icon: <FaHandPointRight />,
    accent: "#3b82f6"
  },
  {
    title: "INNOVATE",
    desc: "Deliver innovative, future-ready solutions for modern business challenges.",
    img: banner3,
    icon: <FaLightbulb />,
    accent: "#1037b9"
  },
  {
    title: "EXCEL",
    desc: "Achieve operational excellence through strategic partnership & execution.",
    img: banner5,
    icon: <FaHandshake />,
    accent: "#8b5cf6"
  }
];

// --- Animation Variants ---
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

// --- Sub-Components ---

const SectionHeader = ({ eyebrow, title, subtitle, centered = true }) => (
  <div className={`mb-5 ${centered ? "text-center" : ""}`} style={{ maxWidth: centered ? "700px" : "none", margin: centered ? "0 auto 3rem" : "0 0 3rem" }}>
    {eyebrow && (
      <motion.span 
        className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill mb-3 font-bold"
        style={{
          background: "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(20,184,166,0.08))",
          color: "#1c1b2e",
          fontWeight: "600",
          fontSize: "0.82rem",
          border: "1px solid rgba(16,185,129,0.25)",
          letterSpacing: "1px"
        }}
        initial={{ opacity: 0, y: -10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <FaShieldHeart size={13} />
        {eyebrow}
      </motion.span>
    )}
    
    <motion.h2 
      className="font-bold mb-3 text-primary"
      style={{ 
        fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
        color: "#0f172a",
        lineHeight: "1.2",
        letterSpacing: "-0.02em"
      }}
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1 }}
    >
      {title}
    </motion.h2>
    
    {subtitle && (
      <motion.p 
        className="mb-0"
        style={{ 
          fontSize: "1.1rem", 
          color: "#475569", 
          lineHeight: "1.7",
          maxWidth: centered ? "600px" : "none",
          margin: centered ? "0 auto" : "0"
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
      >
        {subtitle}
      </motion.p>
    )}
    
    {/* Decorative divider */}
    <motion.div 
      className="mx-auto mt-4 rounded-pill"
      style={{ 
        width: '60px', 
        height: '3px', 
        background: "linear-gradient(90deg, #3b82f6, #6366f1)" 
      }}
      initial={{ scaleX: 0, width: 0 }}
      whileInView={{ scaleX: 1, width: '60px' }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.3 }}
    />
  </div>
);

const ModernImageSlider = ({ images, height = "450px" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isHovered, images.length]);

  return (
    <motion.div 
      className="position-relative rounded-4 overflow-hidden mx-auto"
      style={{ 
        height, 
        maxWidth: "600px",
        boxShadow: "0 5px 10px -10px rgba(0, 0, 0, 0.15)",
        border: "1px solid rgba(255,255,255,0.5)"
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.005 }}
      transition={{ duration: 0.4 }}
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={currentIndex}
          src={images[currentIndex]}
          alt={`ATRIC Portfolio ${currentIndex + 1}`}
          className="d-block w-100 h-100"
          style={{ objectFit: "cover" }}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.7 }}
        />
      </AnimatePresence>
      
      {/* Clean gradient overlay */}
      <div className="position-absolute inset-0" 
        style={{ background: "linear-gradient(to top, rgba(15,23,42,0.6) 0%, transparent 60%)" }} 
      />
      
      {/* Minimal navigation */}
      <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center gap-2 pb-4">
        {images.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className="border-0 p-0 rounded-pill"
            style={{
              width: idx === currentIndex ? "32px" : "10px",
              height: "10px",
              background: idx === currentIndex ? "#fff" : "rgba(255,255,255,0.5)",
              transition: "all 0.3s ease",
              cursor: "pointer"
            }}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </div>
    </motion.div>
  );
};

const StatItem = ({ value, label, accent }) => (
  <motion.div 
    className="text-center px-3"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <span className="d-block fw-bold mb-1" style={{ 
      fontSize: "2rem", 
      color: "#0f172a",
      lineHeight: 1 
    }}>
      {value}
    </span>
    <span className="text-muted small text-uppercase" style={{ 
      letterSpacing: "0.8px", 
      fontSize: "0.75rem",
      fontWeight: "600"
    }}>
      {label}
    </span>
  </motion.div>
);

const PillarCard = ({ pillar, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div 
      className="card border-0 text-center mx-auto"
      style={{ 
        maxWidth: "340px",
        borderRadius: "20px",
        background: "white",
        boxShadow: "0 4px 10px -4px rgba(0, 0, 0, 0.08)",
        border: "1px solid rgba(0,0,0,0.04)"
      }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ y: -6 }}
    >
      <div className="card-body p-4">
        {/* Icon */}
        <motion.div 
          className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
          style={{
            width: "64px", height: "64px",
            background: `linear-gradient(135deg, ${pillar.accent}15, ${pillar.accent}08)`,
            border: `2px solid ${pillar.accent}30`,
            color: pillar.accent
          }}
          whileHover={{ scale: 1.08 }}
        >
          {React.cloneElement(pillar.icon, { size: 26 })}
        </motion.div>
        
        {/* Title */}
        <h4 className="font-bold mb-3" style={{ 
          color: "#0f172a", 
          fontSize: "1.25rem",
          letterSpacing: "0.5px"
        }}>
          {pillar.title}
        </h4>
        
        {/* Description */}
        <p className="text-muted mb-0" style={{ 
          lineHeight: "1.65", 
          fontSize: "0.95rem" 
        }}>
          {pillar.desc}
        </p>
        
        {/* Subtle bottom accent */}
        <motion.div 
          style={{
            height: "3px",
            width: "40px",
            background: pillar.accent,
            borderRadius: "2px",
            margin: "1.25rem auto 0",
            opacity: isHovered ? 1 : 0.6
          }}
        />
      </div>
    </motion.div>
  );
};

const ValueItem = ({ value, index }) => (
  <motion.div
    className="col-6 col-md-4 col-lg-3"
    initial={{ opacity: 0, scale: 0.95 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.05 }}
    whileHover={{ y: -3 }}
  >
    <div className="text-center p-3">
      <motion.div 
        className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
        style={{
          width: "40px", height: "40px",
          background: "linear-gradient(135deg, #3b82f6, #6366f1)",
          color: "white",
          fontWeight: "600",
          fontSize: "0.8rem",
          boxShadow: "0 4px 7px rgba(59,130,246,0.3)"
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </motion.div>
      <span className="d-block fw-semibold mb-1" style={{ 
        color: "#0f172a", 
        fontSize: "0.95rem" 
      }}>
        {value.name}
      </span>
      <span className="text-muted small" style={{ fontSize: "0.8rem", lineHeight: "1.4" }}>
        {value.desc}
      </span>
    </div>
  </motion.div>
);

// --- Main Component ---

export default function AboutUsContent() {
  return (
    <section className="position-relative" style={{ 
      background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
      padding: "5rem 0",
      overflow: "hidden"
    }}>
      
      {/* Subtle background decoration */}
      <div className="position-absolute top-0 start-50 translate-middle-x" style={{
        width: "100%", maxWidth: "1200px", height: "1px",
        background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.15), transparent)"
      }} />
      
      <div className="container" style={{ maxWidth: "1200px" }}>
        
        {/* Hero Section - Centered */}
        <motion.div 
          className="row g-5 align-items-center mb-5"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="col-lg-6 mx-auto text-center order-2 order-lg-1">
            <motion.div variants={fadeInUp}>
              <SectionHeader 
                eyebrow="ABOUT ATRIC"
                title="Empowering Business Through Excellence"
                subtitle="ATRIC Business Consulting Limited is a premier professional services firm offering Management Accounting, Taxation, Internal Audit, and Financial Management services across Tanzania."
              />
              
              {/* Mission highlight */}
              <motion.blockquote 
                className="border-0 mb-4 mx-auto"
                style={{ 
                  maxWidth: "520px",
                  borderLeft: `3px solid #3b82f6`,
                  paddingLeft: "1.25rem",
                  textAlign: "left"
                }}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <p className="mb-0 font-semi-bold mb-3 text-center" style={{ 
                  color: "#334155", 
                  fontSize: "1.05rem", 
                  lineHeight: "1.7",
                  
                }}>
                  Our mission is to provide quality and timely professional services while upholding the highest standards of integrity, professionalism, and client confidentiality.
                </p>
              </motion.blockquote>
              
              {/* Stats */}
              <motion.div 
                className="d-flex justify-content-center pt-3 mt-4"
                style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
              >
                <StatItem value="10+" label="Years Experience" accent="#3b82f6" />
                <div className="vr" style={{ background: "rgba(0,0,0,0.1)" }}></div>
                <StatItem value="500+" label="Happy Clients" accent="#10b981" />
                <div className="vr" style={{ background: "rgba(0,0,0,0.1)" }}></div>
                <StatItem value="20+" label="Expert Consultants" accent="#8b5cf6" />
              </motion.div>
            </motion.div>
          </div>
          
          <div className="col-lg-6 mx-auto text-center order-1 order-lg-2">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <ModernImageSlider images={aboutImages} />
            </motion.div>
          </div>
        </motion.div>

        {/* Vision & Mission - Centered Cards */}
        <div className="text-center mb-5">
          <SectionHeader 
            title="Our Direction"
            subtitle="Guided by a clear vision and mission to deliver exceptional value."
          />
        </div>
        
        <motion.div 
          className="row g-4 justify-content-center mb-5"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="col-md-6 col-lg-5">
            <motion.div 
              className="card border-0 text-center h-100 mx-auto"
              style={{
                borderRadius: "20px",
                background: "white",
                boxShadow: "0 4px 5px -4px rgba(0, 0, 0, 0.08)",
                border: "1px solid rgba(0,0,0,0.04)",
                maxWidth: "420px"
              }}
              variants={fadeInUp}
              whileHover={{ y: -5 }}
            >
              <div className="card-body p-5">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                  style={{
                    width: "68px", height: "68px",
                    background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                    color: "white",
                    boxShadow: "0 10px 25px rgba(59,130,246,0.3)"
                  }}
                >
                  <FaEye size={28} />
                </div>
                <h4 className="font-bold mb-3" style={{ color: "#0f172a", fontSize: "1.3rem" }}>Our Vision</h4>
                <p className="text-muted mb-0" style={{ lineHeight: "1.7" }}>
                  To be the leading consulting company in Africa providing exceptional, transformative professional services that drive sustainable growth.
                </p>
              </div>
            </motion.div>
          </div>
          
          <div className="col-md-6 col-lg-5">
            <motion.div 
              className="card border-0 text-center h-100 mx-auto"
              style={{
                borderRadius: "20px",
                background: "white",
                boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.08)",
                border: "1px solid rgba(0,0,0,0.04)",
                maxWidth: "420px"
              }}
              variants={fadeInUp}
              whileHover={{ y: -5 }}
            >
              <div className="card-body p-5">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                  style={{
                    width: "68px", height: "68px",
                    background: "linear-gradient(135deg, #1029b9, #1448b8)",
                    color: "white",
                    boxShadow: "0 10px 25px rgba(18, 29, 25, 0.3)"
                  }}
                >
                  <FaScaleBalanced size={28} />
                </div>
                <h4 className="font-bold mb-3" style={{ color: "#0f172a", fontSize: "1.3rem" }}>Our Mission</h4>
                <p className="text-muted mb-0" style={{ lineHeight: "1.7" }}>
                  To deliver quality, timely, and innovative professional services that exceed client expectations while maintaining the highest ethical standards.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Pillars Section - Centered */}
        <div className="text-center mb-5">
          <SectionHeader 
            title="Our Approach"
            subtitle="Three pillars that define how we deliver value to our clients."
          />
        </div>
        
        <motion.div 
          className="row g-4 justify-content-center mb-5"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {pillars.map((pillar, index) => (
            <div className="col-md-6 col-lg-4" key={pillar.title}>
              <PillarCard pillar={pillar} index={index} />
            </div>
          ))}
        </motion.div>

        {/* Core Values - Centered Grid */}
        <div className="text-center mb-5">
          <SectionHeader 
            title="Our Core Values"
            subtitle="The principles that guide every decision and interaction."
          />
        </div>
        
        <motion.div 
          className="row g-3 justify-content-center mb-5"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          style={{ maxWidth: "1000px", margin: "0 auto" }}
        >
          {coreValues.map((value, index) => (
            <ValueItem key={value.name} value={value} index={index} />
          ))}
        </motion.div>

        {/* Centered CTA */}
        <motion.div 
          className="text-center pt-4 mt-4"
          style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <p className="mb-4" style={{ 
            color: "#334155", 
            fontSize: "1.1rem", 
            fontWeight: "500" 
          }}>
            Ready to transform your business?
          </p>
          <motion.button 
            className="btn px-5 py-3 rounded-pill d-inline-flex align-items-center gap-2"
            style={{
              background: "linear-gradient(135deg, #031161, #1053b9)",
              border: "none",
              color: "white",
              fontWeight: "600",
              fontSize: "1rem",
              boxShadow: "0 8px 25px rgba(16, 185, 129, 0.35)"
            }}
            whileHover={{ 
              y: -3, 
              boxShadow: "0 12px 35px rgba(16, 185, 129, 0.45)" 
            }}
            whileTap={{ scale: 0.98 }}
          >
            Schedule a Consultation
            <FaArrowRight size={18} />
          </motion.button>
        </motion.div>

      </div>

      {/* Global Styles */}
      <style>{`
        .font-bold { font-weight: 700 !important; }
        
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }
        
        /* Smooth scrollbar */
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #f8fafc; }
        ::-webkit-scrollbar-thumb { 
          background: linear-gradient(180deg, #cbd5e1, #94a3b8); 
          border-radius: 4px; 
        }
        
        /* Focus accessibility */
        button:focus-visible {
          outline: 3px solid #3b82f6;
          outline-offset: 2px;
          border-radius: 8px;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          section { padding: 3rem 0 !important; }
          .card-body.p-5 { padding: 2rem !important; }
          .card-body.p-4 { padding: 1.5rem !important; }
        }
      `}</style>
    </section>
  );
}